import { useEffect, useMemo, useRef, useState, MouseEvent } from "react";
import { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface SideNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface ServicesSideNavProps {
  items: SideNavItem[];
  variant?: "sticky" | "inline";
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const useActiveSection = (ids: string[]) => {
  const [activeId, setActiveId] = useState<string>(ids[0] ?? "");
  const activeRef = useRef(activeId);
  activeRef.current = activeId;
  // Don't rewrite window.location.hash until after the initial scroll
  // settles — otherwise we clobber an inbound deep-link (e.g. /services#strategy)
  // mid-animation and the user lands on the wrong section.
  const allowHashWrite = useRef(false);

  useEffect(() => {
    if (ids.length === 0) return;

    let raf = 0;
    const compute = () => {
      raf = 0;
      const line = window.innerHeight * 0.35;
      let bestId = ids[0];
      let bestDelta = -Infinity;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        const delta = top - line;
        if (delta <= 0 && delta > bestDelta) {
          bestDelta = delta;
          bestId = id;
        }
      }
      if (bestId !== activeRef.current) {
        setActiveId(bestId);
        if (allowHashWrite.current && window.location.hash !== `#${bestId}`) {
          history.replaceState(null, "", `#${bestId}`);
        }
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const enableTimer = window.setTimeout(() => {
      allowHashWrite.current = true;
    }, 600);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.clearTimeout(enableTimer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ids]);

  return activeId;
};

const handleNavClick = (
  e: MouseEvent<HTMLAnchorElement>,
  id: string
) => {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? "auto" : "smooth",
    block: "start",
  });
  history.replaceState(null, "", `#${id}`);
};

const ServicesSideNav = ({ items, variant = "sticky" }: ServicesSideNavProps) => {
  const { t } = useTranslation();
  const ids = useMemo(() => items.map((i) => i.id), [items]);
  const activeId = useActiveSection(ids);
  const activeIndex = Math.max(0, items.findIndex((i) => i.id === activeId));

  if (variant === "inline") {
    return (
      <nav
        aria-label={t("servicesPage.sideNavAria")}
        className="lg:hidden sticky top-16 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-8 bg-background/85 backdrop-blur-md border-b border-border/50"
      >
        <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {items.map((item, idx) => {
            const isActive = activeId === item.id;
            const Icon = item.icon;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleNavClick(e, item.id)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-colors duration-200 h-full",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    isActive
                      ? "bg-gradient-brand text-primary-foreground border-transparent"
                      : "border-border/60 text-muted-foreground hover:text-foreground hover:border-primary/40"
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex flex-col min-w-0 leading-tight">
                    <span className="font-mono text-[10px] opacity-70">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="truncate">{item.label}</span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }

  // Sticky desktop variant
  const itemHeight = 56; // matches py-3 + content height
  const itemGap = 4;
  const railTop = activeIndex * (itemHeight + itemGap);

  return (
    <aside className="hidden lg:block self-start sticky top-24">
      <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase mb-5 px-2">
        {t("servicesPage.sideNavHeading")}
      </p>

      <nav aria-label={t("servicesPage.sideNavAria")} className="relative">
        {/* Vertical rail */}
        <div
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-px bg-border/60"
        />
        {/* Active rail segment */}
        <div
          aria-hidden
          className="absolute left-[-1px] w-[3px] rounded-full bg-gradient-brand shadow-glow-primary transition-[top] duration-300 ease-out"
          style={{
            top: `${railTop}px`,
            height: `${itemHeight}px`,
          }}
        />

        <ul className="space-y-1 pl-4">
          {items.map((item, idx) => {
            const isActive = activeId === item.id;
            const Icon = item.icon;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleNavClick(e, item.id)}
                  style={{ height: `${itemHeight}px` }}
                  className={cn(
                    "group flex items-center gap-3 pr-3 pl-2 rounded-lg transition-colors duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    isActive
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <span
                    className={cn(
                      "shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200",
                      isActive
                        ? "bg-gradient-brand text-primary-foreground shadow-glow-primary"
                        : "bg-muted/40 text-muted-foreground group-hover:bg-muted/60 group-hover:text-foreground"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span
                      className={cn(
                        "font-mono text-[10px] tracking-widest uppercase leading-none transition-colors",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={cn(
                        "text-sm mt-1 leading-tight",
                        isActive ? "font-semibold" : "font-medium"
                      )}
                    >
                      {item.label}
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default ServicesSideNav;
